import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ArrowLeft, Save, X, Users, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { employeesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = Boolean(id);
  const { employee: currentUser } = useAuth();

  // Check if current user is superadmin (developer)
  const isSuperAdmin = currentUser?.role === 'developer' || currentUser?.email === 'development@farmindie.com';

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role: 'staff',
      department: '',
      is_active: true,
      is_delivery_boy: false,
      delivery_areas: '',
      vehicle_type: '',
      vehicle_number: '',
      license_number: '',
      is_available: false,
    }
  });

  // Watch the is_delivery_boy field to show/hide delivery boy fields
  const isDeliveryBoy = watch('is_delivery_boy');

  // Fetch employee data if in edit mode
  const { data: employeeData, isLoading: isLoadingData } = useQuery(
    ['employee', id],
    () => employeesAPI.getById(id),
    {
      enabled: isEditMode,
      onSuccess: (data) => {
        const employee = data.data?.data || data.data;
        
        // Convert delivery_areas from JSON array to comma-separated string
        let deliveryAreasString = '';
        if (employee.delivery_areas) {
          if (Array.isArray(employee.delivery_areas)) {
            deliveryAreasString = employee.delivery_areas.join(', ');
          } else if (typeof employee.delivery_areas === 'string') {
            try {
              const parsed = JSON.parse(employee.delivery_areas);
              deliveryAreasString = Array.isArray(parsed) ? parsed.join(', ') : employee.delivery_areas;
            } catch {
              deliveryAreasString = employee.delivery_areas;
            }
          }
        }
        
        reset({
          name: employee.name || '',
          email: employee.email || '',
          phone: employee.phone || '',
          role: employee.role || 'staff',
          department: employee.department || '',
          is_active: employee.is_active !== undefined ? Boolean(employee.is_active) : true,
          is_delivery_boy: Boolean(employee.is_delivery_boy),
          delivery_areas: deliveryAreasString,
          vehicle_type: employee.vehicle_type || '',
          vehicle_number: employee.vehicle_number || '',
          license_number: employee.license_number || '',
          is_available: Boolean(employee.is_available),
        });
      },
    }
  );

  const mutation = useMutation(
    async (data) => {
      if (isEditMode) {
        return employeesAPI.update(id, data);
      } else {
        return employeesAPI.create(data);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditMode ? 'Employee updated successfully!' : 'Employee created successfully!');
        queryClient.invalidateQueries('employees');
        navigate('/employees');
      },
      onError: (error) => {
        console.error('Mutation error:', error);
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const onSubmit = async (data) => {
    mutation.mutate(data);
  };

  const handleCancel = () => {
    navigate('/employees');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="h-6 w-6" />
                {isEditMode ? 'Edit Employee' : 'Create New Employee'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isEditMode ? 'Update employee information' : 'Add a new employee to your team'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        {isLoadingData ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Employee Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Employee Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Employee name is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter employee name"
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
              )}
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Please enter a valid email address'
                    }
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            {/* Role and Department */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('role', { required: 'Role is required' })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select role</option>
                  {isSuperAdmin && (
                    <option value="developer">Developer</option>
                  )}
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="staff">Staff</option>
                </select>
                {errors.role && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.role.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  {...register('department')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter department"
                />
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('is_active')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                Active Employee
              </label>
            </div>

            {/* Delivery Boy Section */}
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <Truck className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delivery Boy Settings
                </h3>
              </div>

              {/* Is Delivery Boy Checkbox */}
              <div className="flex items-center mb-6">
                <input
                  type="checkbox"
                  {...register('is_delivery_boy')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                  Mark as Delivery Boy
                </label>
              </div>

              {/* Delivery Boy Fields - Only show if is_delivery_boy is checked */}
              {isDeliveryBoy && (
                <div className="space-y-6 pl-6 border-l-2 border-indigo-200 dark:border-indigo-800">
                  {/* Delivery Areas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Delivery Areas (Pincodes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register('delivery_areas', { 
                        required: isDeliveryBoy ? 'Delivery areas are required for delivery boys' : false 
                      })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      placeholder="e.g., 400001, 400002, 400003"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Enter comma-separated pincodes for delivery areas
                    </p>
                    {errors.delivery_areas && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.delivery_areas.message}</p>
                    )}
                  </div>

                  {/* Vehicle Type and Number */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vehicle Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('vehicle_type', { 
                          required: isDeliveryBoy ? 'Vehicle type is required' : false 
                        })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">Select vehicle type</option>
                        <option value="bike">Bike</option>
                        <option value="scooter">Scooter</option>
                        <option value="car">Car</option>
                        <option value="van">Van</option>
                        <option value="truck">Truck</option>
                      </select>
                      {errors.vehicle_type && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.vehicle_type.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vehicle Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register('vehicle_number', { 
                          required: isDeliveryBoy ? 'Vehicle number is required' : false 
                        })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="e.g., MH01AB1234"
                      />
                      {errors.vehicle_number && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.vehicle_number.message}</p>
                      )}
                    </div>
                  </div>

                  {/* License Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      License Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register('license_number', { 
                        required: isDeliveryBoy ? 'License number is required' : false 
                      })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      placeholder="e.g., DL1234567890"
                    />
                    {errors.license_number && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.license_number.message}</p>
                    )}
                  </div>

                  {/* Is Available */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('is_available')}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                      Available for Deliveries
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isLoading}
                className="flex-1 sm:flex-none px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {mutation.isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditMode ? 'Update Employee' : 'Create Employee'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeForm;